const _excluded = ["style", "className", "topItems", "bottomItems", "selectedId", "renderList", "renderItem", "color"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import * as analytics from '@adsk/acs-web-analytics';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { ArrowAlignRightIcon, ArrowAlignLeftIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import { ANALYTICS_TYPES, EXTERNAL_ICON_CLASSNAME } from './consts';
import { isActive } from './isActive';
import { useSideNavigation } from './SideNavigationContext';
import SideNavigationItem from './SideNavigationItem';
import SideNavigationItemLoader from './SideNavigationItemLoader';
import SideNavigationList from './SideNavigationList';
function topItemsLoadingData() {
  let length = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 10;
  return Array.from({
    length
  }).map((_, key) => ({
    key: "SideNavigation__loader-id--".concat(key),
    loading: true,
    name: '',
    url: ''
  }));
}
/**
 * Navigation component, rendering on the left side of page content.
 */
const SideNavigation = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      topItems,
      bottomItems,
      selectedId,
      // TODO: Convert this to be used as a render prop instead of component (breaking change)
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components, @adsk/react-render-props/component-to-render-prop
      renderList: List = SideNavigationList,
      // TODO: Convert this to be used as a render prop instead of component (breaking change)
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components, @adsk/react-render-props/component-to-render-prop
      renderItem: Item = SideNavigationItem,
      color: _color // this prop is ignored to fix an error with Flex getting wrong color type
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("./i18n/".concat(locale, ".json"));
  });
  const {
    collapsed,
    setCollapsed
  } = useSideNavigation();
  const onToggle = () => {
    analytics.track(ANALYTICS_TYPES.SIDENAVIGATION_EXPANDCOLLAPSE_CLICK, {
      is_collapsed: !collapsed
    });
    setCollapsed(val => !val);
  };
  const onClickTool = item => {
    analytics.track(ANALYTICS_TYPES.SIDENAVIGATION_TOOL_CLICK, {
      tool_id: item.key,
      tool_name: item.name
    });
  };
  const IconComponent = collapsed ? ArrowAlignRightIcon : ArrowAlignLeftIcon;
  return /*#__PURE__*/React.createElement(Flex, _extends({}, props, {
    className: className,
    ref: ref,
    backgroundColor: "charcoal050",
    flexDirection: "column",
    justifyContent: "space-between",
    minWidth: collapsed ? undefined : '220px',
    style: style
  }), topItems !== undefined && /*#__PURE__*/React.createElement(Box, {
    overflow: "auto"
  }, /*#__PURE__*/React.createElement(List, null, (topItems.length ? topItems : topItemsLoadingData()).map((item, index) => {
    var _item$renderIcon;
    return /*#__PURE__*/React.createElement(Item, {
      key: item.id || item.key,
      item: item,
      active: isActive(item, selectedId),
      collapsed: collapsed,
      "data-testid": item['data-testid'],
      onClick: () => onClickTool(item)
    }, item.loading && /*#__PURE__*/React.createElement(SideNavigationItemLoader, {
      collapsed: collapsed,
      index: index
    }), !item.loading && /*#__PURE__*/React.createElement(Tooltip, {
      placement: Tooltip.PLACEMENTS.RIGHT,
      content: collapsed ? item.name : '',
      delayShow: 0,
      renderTarget: props => /*#__PURE__*/React.createElement(Tooltip.Target, _extends({}, props, {
        shouldWrapChildren: false
      }))
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    }, item.iconUrl ? /*#__PURE__*/React.createElement(Box, {
      className: EXTERNAL_ICON_CLASSNAME,
      backgroundColor: isActive(item, selectedId) ? 'adskBlue500' : 'charcoal700',
      width: "24px",
      height: "24px",
      style: {
        maskImage: "url(".concat(item.iconUrl, ")")
      }
    }) : (_item$renderIcon = item.renderIcon) === null || _item$renderIcon === void 0 ? void 0 : _item$renderIcon.call(item, {
      size: 24
    }), !collapsed && /*#__PURE__*/React.createElement(Text, {
      marginLeft: 12,
      style: {
        width: 160
      }
    }, /*#__PURE__*/React.createElement(OverflowTooltip, null, item.name)))));
  }))), /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0,
    paddingBottom: "4px"
  }, topItems && /*#__PURE__*/React.createElement(Divider, {
    style: {
      width: 'auto',
      margin: '0 8px'
    }
  }), bottomItems && /*#__PURE__*/React.createElement(List, null, bottomItems.map(item => {
    var _item$renderIcon2;
    return /*#__PURE__*/React.createElement(Item, {
      key: item.id || item.key,
      item: item,
      collapsed: collapsed,
      active: isActive(item, selectedId),
      onClick: () => onClickTool(item)
    }, /*#__PURE__*/React.createElement(Tooltip, {
      placement: Tooltip.PLACEMENTS.RIGHT,
      content: collapsed ? item.name : '',
      delayShow: 0,
      renderTarget: props => /*#__PURE__*/React.createElement(Tooltip.Target, _extends({}, props, {
        shouldWrapChildren: false
      }))
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    }, item.iconUrl ? /*#__PURE__*/React.createElement(Box, {
      className: EXTERNAL_ICON_CLASSNAME,
      backgroundColor: isActive(item, selectedId) ? 'adskBlue500' : 'charcoal700',
      width: "24px",
      height: "24px",
      style: {
        maskImage: "url(".concat(item.iconUrl, ")")
      }
    }) : (_item$renderIcon2 = item.renderIcon) === null || _item$renderIcon2 === void 0 ? void 0 : _item$renderIcon2.call(item, {
      size: 24
    }), !collapsed && /*#__PURE__*/React.createElement(Box, {
      typography: "ellipsis",
      marginLeft: 12,
      width: "160px"
    }, item.name))));
  })), /*#__PURE__*/React.createElement(Tooltip, {
    placement: Tooltip.PLACEMENTS.RIGHT,
    content: collapsed ? T.translate('EXPAND') : T.translate('COLLAPSE'),
    renderTarget: props => /*#__PURE__*/React.createElement(Tooltip.Target, _extends({}, props, {
      shouldWrapChildren: false
    })),
    delayShow: 0
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'inline-block'
    }
  }, /*#__PURE__*/React.createElement(BasicButton, {
    onClick: onToggle,
    style: [theme.typography.bodyMedium, {
      padding: 6,
      margin: '4px 6px',
      color: theme.colors.charcoal700,
      ':hover': {
        background: theme.colors.charcoal100
      },
      ':active': {
        background: theme.colors.charcoal200
      }
    }]
  }, /*#__PURE__*/React.createElement(IconComponent, {
    size: 24
  }))))));
});
SideNavigation.displayName = 'SideNavigation';
SideNavigation.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Links to display above divider; main links */
  topItems: PropTypes.array,
  /** Links to display below divider; footer links */
  bottomItems: PropTypes.array,
  /** The currently selected item Id (or key) */
  selectedId: PropTypes.string,
  /** Optional override for rendering custom navigation list */
  renderList: PropTypes.func,
  /** Optional override for rendering custom navigation items */
  renderItem: PropTypes.func
};
export default SideNavigation;
//# sourceMappingURL=SideNavigation.js.map